import { StyledButton } from "@aureskonnect/react-ui";
import { ELECTRON_MODE, IP_ADDRESS_REGEX, WEBVIEW_MODE } from "@constants";
import { AvForm } from "availity-reactstrap-validation";
import { t } from "i18next";
import { useSnapshot } from "valtio";

import React from "react";
import { Row } from "react-bootstrap";
import { FaTimes, FaWifi } from "react-icons/fa";

import { setIsMiddlewareApiUrlExists, setMiddlewareApiUrl } from "@store";

import { FlexboxGrid } from "@components/common/FlexboxGrid";
import SoftaveraLogo from "@components/SvgIcons/SoftaveraLogo";

import {
    kioskStore,
    setIsKeyboardOpened,
    setKeyboardInputsValues,
} from "@pages/Kiosk/store";

import MessageErrorComponent from "./MessageErrorComponent";

type MiddlewareApiUrlInputWrapperType = {
    isInputValueInvalid: boolean;
    setIsInputValueInvalid: React.Dispatch<React.SetStateAction<boolean>>;
    isFieldRequired: boolean;
    setIsUpdateMiddlware?: React.Dispatch<React.SetStateAction<boolean>>;
    isUpdateMiddlware?: boolean;
    setIsSystemConfirmationOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    keyboard: React.MutableRefObject<any>;
};

export function MiddlewareApiUrlInputWrapper({
    isInputValueInvalid,
    setIsInputValueInvalid,
    isFieldRequired,
    setIsUpdateMiddlware,
    isUpdateMiddlware,
    setIsSystemConfirmationOpen,
    keyboard,
}: MiddlewareApiUrlInputWrapperType): JSX.Element {
    return (
        <div
            style={{
                minHeight: "100vh",
                minWidth: "100vw",
                background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                display: "grid",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "2rem",
                gridTemplateRows: "20vh 50vh",
            }}
        >
            <Row>
                <div style={{ marginBottom: "2rem" }}>
                    <SoftaveraLogo />
                </div>
            </Row>
            <div style={{ width: "100%", maxWidth: "500px" }}>
                {/* Header with Icon */}
                <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                    <h1
                        style={{
                            font: "normal 32px/38px Segoe UI",
                            letterSpacing: "0px",
                            color: "#1f2937",
                            opacity: 1,
                            whiteSpace: "nowrap",
                            marginBottom: "0.5rem",
                            fontWeight: "600",
                        }}
                    >
                        {t("Server machine IP Address")}
                    </h1>
                    <p
                        style={{
                            color: "#65b6d5",
                            fontSize: "16px",
                            margin: 0,
                        }}
                    >
                        {t("Enter your server details to establish connection")}
                    </p>
                </div>

                {/* Main Card */}
                <MiddlewareApiUrlInputCard
                    isInputValueInvalid={isInputValueInvalid}
                    setIsInputValueInvalid={setIsInputValueInvalid}
                    isFieldRequired={isFieldRequired}
                    setIsUpdateMiddlware={setIsUpdateMiddlware}
                    isUpdateMiddlware={isUpdateMiddlware}
                    setIsSystemConfirmationOpen={setIsSystemConfirmationOpen}
                    keyboard={keyboard}
                />

                {/* Footer */}
                <div style={{ textAlign: "center", marginTop: "2rem" }}>
                    <p
                        style={{
                            fontSize: "12px",
                            color: "#9ca3af",
                            margin: 0,
                        }}
                    >
                        {t(
                            "Ensure your server is running and accessible on the network"
                        )}
                    </p>
                </div>
            </div>

            {/* Add CSS for spinner animation */}
            <style>{`
       @keyframes spin {
           0% { transform: rotate(0deg); }
           100% { transform: rotate(360deg); }
       }
   `}</style>
        </div>
    );
}

export function MiddlewareApiUrlInputCard({
    isInputValueInvalid,
    setIsInputValueInvalid,
    isFieldRequired,
    setIsUpdateMiddlware,
    isUpdateMiddlware,
    setIsSystemConfirmationOpen,
    keyboard,
}: MiddlewareApiUrlInputWrapperType): JSX.Element {
    const { keyboardInputsValues } = useSnapshot(kioskStore);

    const [inputValues, setInputValues] = React.useState<string>(
        Object.values(keyboardInputsValues)[0]
    );
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [isConnecting, setIsConnecting] = React.useState<boolean>(false);

    function handleInputOnChangeEvent(
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        const inputValue = event.target.value;
        setInputValues(inputValue);
        setKeyboardInputsValues({ ipAddress: inputValue });

        // Clear error message when user starts typing
        if (errorMessage) {
            setErrorMessage("");
        }

        if (keyboard.current) {
            keyboard.current.setInput(inputValue);
        }
    }

    function handleValidateButtonOnClickEvent() {
        if (Object.values(keyboardInputsValues)[0] === "" && isFieldRequired) {
            const errMsg = t("This field is required");
            setErrorMessage(errMsg);
            return;
        }

        if (!IP_ADDRESS_REGEX.test(inputValues)) {
            const errMsg = t("Invalid IP address");
            setErrorMessage(errMsg);
            return;
        }

        // Show connecting state
        setIsConnecting(true);

        // Simulate connection process (replace with actual connection logic)
        setTimeout(() => {
            setMiddlewareApiUrl(inputValues);
            setIsMiddlewareApiUrlExists(true);
            setIsKeyboardOpened(false);
            setIsUpdateMiddlware?.(false);
            setIsSystemConfirmationOpen?.(false);
            setKeyboardInputsValues({
                ipAddress: "",
            });
            setInputValues("");
            setIsConnecting(false);
        }, 1500);
    }

    function handleCloseApplication() {
        const closeApp = "close_app";
        const url = `http://localhost:6009/api/v1/kiosk?systemAction=${closeApp}`;

        if (isUpdateMiddlware) {
            setIsUpdateMiddlware?.(false);
            setIsSystemConfirmationOpen?.(false);
            setKeyboardInputsValues({
                ipAddress: "",
            });
            setInputValues("");
            setErrorMessage("");
            if (keyboard.current) {
                keyboard.current.setInput("");
            }
            return;
        }

        if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
            fetch(url).catch((error) =>
                console.log(
                    `Error while using system action with action: "${closeApp}" and Error: "${error}"`
                )
            );
        } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
            (window as RendererWindow).electronAPI?.sendQuitEvent();
        }
    }

    function handleClearInput() {
        setKeyboardInputsValues({
            ipAddress: "",
        });
        setInputValues("");
        setIsKeyboardOpened(false);
        setIsInputValueInvalid(false);
        setErrorMessage("");
    }

    React.useEffect(() => {
        setInputValues(Object.values(keyboardInputsValues)[0]);
    }, [keyboardInputsValues]);

    const isConnectDisabled =
        Object.values(keyboardInputsValues)[0] === "" ||
        Object.values(keyboardInputsValues)[0] === undefined ||
        isInputValueInvalid === true ||
        isConnecting;
    return (
        <div
            style={{
                backgroundColor: "#ffffff",
                borderRadius: "20px",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                border: "1px solid #e5e7eb",
                padding: isUpdateMiddlware ? "5rem" : "3rem",
                width: isUpdateMiddlware ? "90%" : "32rem",
            }}
        >
            <FlexboxGrid
                justifyContentCentered={true}
                alignItemsCentered={true}
                gap="30px"
                className="flex-column"
            >
                <AvForm style={{ width: "100%" }}>
                    {/* Input Label */}
                    <label
                        htmlFor="middlewareApiUrlInput"
                        style={{
                            display: "block",
                            fontSize: "16px",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "12px",
                        }}
                    >
                        {t("Middleware IP Address")}
                    </label>

                    {/* Input Container */}
                    <div
                        style={{
                            position: "relative",
                            marginBottom: "1.5rem",
                        }}
                    >
                        {/* Left Icon */}
                        <div
                            style={{
                                position: "absolute",
                                left: "16px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                zIndex: 1,
                            }}
                        >
                            <FaWifi
                                style={{
                                    fontSize: "20px",
                                    color: isInputValueInvalid
                                        ? "#ef4444"
                                        : "#9ca3af",
                                }}
                            />
                        </div>

                        <input
                            style={{
                                height: "65px",
                                width: "100%",
                                paddingLeft: "56px",
                                paddingRight: inputValues ? "56px" : "20px",
                                background: isInputValueInvalid
                                    ? "#fef2f2"
                                    : "#ffffff",
                                border: `2px solid ${
                                    isInputValueInvalid ? "#fca5a5" : "#d1d5db"
                                }`,
                                borderRadius: "12px",
                                fontSize: "16px",
                                transition: "all 0.2s ease",
                                outline: "none",
                                boxShadow: "none",
                            }}
                            id="middlewareApiUrlInput"
                            data-testid="middlewareApiUrlInput"
                            name="middlewareApiUrlInput"
                            type="text"
                            placeholder={t("Enter IP address")}
                            onChange={handleInputOnChangeEvent}
                            onBlur={handleInputOnChangeEvent}
                            onFocus={(e) => {
                                e.target.style.borderColor = isInputValueInvalid
                                    ? "#ef4444"
                                    : "#075d9b";
                                e.target.style.boxShadow = `0 0 0 3px ${
                                    isInputValueInvalid
                                        ? "rgba(239, 68, 68, 0.1)"
                                        : "rgba(59, 130, 246, 0.1)"
                                }`;
                                setIsKeyboardOpened(true);
                            }}
                            value={Object.values(keyboardInputsValues)[0] || ""}
                        />

                        {/* Clear Button */}
                        {inputValues && (
                            <button
                                type="button"
                                onClick={handleClearInput}
                                style={{
                                    position: "absolute",
                                    right: "16px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    color: "#6b7280",
                                    transition: "color 0.2s ease",
                                }}
                            >
                                <FaTimes style={{ fontSize: "16px" }} />
                            </button>
                        )}
                    </div>

                    {/* Error Message */}
                    {errorMessage && (
                        <div style={{ marginBottom: "1.5rem" }}>
                            <MessageErrorComponent
                                messageError={errorMessage}
                            />
                        </div>
                    )}
                </AvForm>

                {/* Buttons */}
                <div
                    style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        gap: "16px",
                    }}
                >
                    <StyledButton
                        style={{
                            width: isUpdateMiddlware ? "50%" : "100%",
                            height: "60px",
                            fontSize: "18px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "12px",
                            borderRadius: "12px",
                            border: "none",
                            boxShadow: isConnectDisabled
                                ? "none"
                                : "0 10px 25px -5px rgba(85, 115, 153, 0.4)",
                            transition: "all 0.2s ease",
                            background: isConnectDisabled
                                ? "#557399"
                                : "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
                        }}
                        onClick={handleValidateButtonOnClickEvent}
                        block={true}
                        rounded={true}
                        variant="primary"
                        disabled={isConnectDisabled}
                    >
                        {isConnecting ? (
                            <>
                                <div
                                    style={{
                                        width: "20px",
                                        height: "20px",
                                        border: "2px solid #ffffff",
                                        borderTop: "2px solid transparent",
                                        borderRadius: "50%",
                                        animation: "spin 1s linear infinite",
                                    }}
                                />
                                {isUpdateMiddlware
                                    ? t("Saving...")
                                    : t("Connecting...")}
                            </>
                        ) : (
                            <>
                                <FaWifi style={{ fontSize: "18px" }} />
                                {t("Validate")}
                            </>
                        )}
                    </StyledButton>

                    <StyledButton
                        style={{
                            width: isUpdateMiddlware ? "50%" : "100%",
                            height: "60px",
                            fontSize: "18px",
                            fontWeight: "500",
                            borderRadius: "12px",
                            backgroundColor: "#ffffff",
                            border: "2px solid #e5e7eb",
                            color: "#374151",
                            transition: "all 0.2s ease",
                        }}
                        onClick={handleCloseApplication}
                        block={true}
                        rounded={true}
                        variant="secondary"
                    >
                        {isUpdateMiddlware ? t("Back") : t("Close app")}
                    </StyledButton>
                </div>
            </FlexboxGrid>
        </div>
    );
}
